import { Axis, AxisBottom, Orientation } from '@visx/axis'
import { ClipPath } from '@visx/clip-path'
import { localPoint } from '@visx/event'
import { GridRows } from '@visx/grid'
import { Group } from '@visx/group'
import { Pattern } from '@visx/pattern'
import { scaleBand, scaleLinear, scaleOrdinal } from '@visx/scale'
import { BarStack, Line } from '@visx/shape'
import { SeriesPoint } from '@visx/shape/lib/types'
import { Text } from '@visx/text'
import { useTooltip, defaultStyles, TooltipWithBounds } from '@visx/tooltip'
import { has, uniq } from 'lodash'
import moment from 'moment'
import { useMemo, useRef } from 'react'
import { DEFAULT_CHART_HUES, seriesStyle } from 'lib/chartColors'
import CommonLegendOrdinal from '../CommonLegendOrdinal'
import { useLegendOverflow } from './useLegendOverflow'
import Tick from '../Tick'
import { ChipSegment } from './ChipSegment'
import { baseKeysByIndex, capKeysByIndex, keysByTotalDesc, stackTopDown } from 'lib/stackedBars'

/** Goal bars are neutral so the actuals carry the hue. */
const GOAL_HUE = `gray`

type BarTooltipData = {
  type: `bar`
  /** Which of the two side-by-side stacks the segment belongs to. */
  stack: `primary` | `compared`
  bar: SeriesPoint<DataType>
  key: string
  index: number
  height: number
  width: number
  x: number
  y: number
  color: string
}

type ScheduledTooltipData = {
  type: `scheduled`
  day: string
  tooltipDisplay?: string
  byChannel: { channel: string; count: number }[]
}

type TooltipData = BarTooltipData | ScheduledTooltipData

type DataType = { day: string; tooltipDisplay?: string; values: { [key: string]: number } }

export type GroupedBarStackProps = {
  width: number
  height: number
  data: DataType[]
  comparedData: DataType[]
  allKeys: string[]
  totals?: { [key: string]: number }
  scheduledData?: {
    day: string
    tooltipDisplay?: string
    value: number
    byChannel: { channel: string; count: number }[]
  }[]
  margin?: { top: number; right: number; bottom: number; left: number }
  events?: boolean
  bottomRightSlot?: React.ReactNode
  now: string | null
  tickFormat: string
  colorMap?: { [key: string]: string }
  valuePrefix?: string
  hideLabels?: boolean
  pairGap?: number
  isGoalComparison?: boolean
  /**
   * The second series normally renders hatched with a faded label, marking it
   * as a comparison period. Pass false when it carries a peer metric rather
   * than a comparison, so it reads as an equal of the primary series.
   */
  dimComparedSeries?: boolean
}

const formatValue = (value: number, prefix?: string): string => {
  const num = Number(value)
  if (isNaN(num) || num === 0) return ``
  // Abbreviate on magnitude. Testing the signed number would send every
  // negative down the unabbreviated branch, printing -22329 instead of -22K.
  const magnitude = Math.abs(num)
  let formatted: string
  if (magnitude >= 1_000_000) {
    formatted = `${Math.round(magnitude / 1_000_000)}M`
  } else if (magnitude >= 1000) {
    formatted = `${Math.round(magnitude / 1000)}K`
  } else {
    formatted = String(Math.round(magnitude))
  }
  // The sign belongs outside the currency symbol: -$22K, not $-22K.
  const withPrefix = prefix ? `${prefix}${formatted}` : formatted
  return num < 0 ? `-${withPrefix}` : withPrefix
}

export const purple3 = `#a44afe`
export const background = `var(--surface)`
const defaultMargin = { top: 40, right: 40, bottom: 0, left: 70 }
const tooltipStyles = {
  ...defaultStyles,
  minWidth: 180,
}

// accessors
const getDate = (d: DataType) => d.day

const sumValues = (d: DataType): number => Object.entries(d.values).reduce((sum, [, value]) => sum + Number(value), 0)

let tooltipTimeout: number

export default function GroupedBarStackGraph({
  width,
  height,
  data,
  comparedData,
  totals,
  allKeys,
  scheduledData,
  bottomRightSlot,
  now,
  events = false,
  margin = defaultMargin,
  tickFormat,
  colorMap,
  valuePrefix,
  hideLabels,
  pairGap,
  isGoalComparison,
  dimComparedSeries = true,
}: GroupedBarStackProps) {
  const { tooltipOpen, tooltipLeft, tooltipTop, tooltipData, hideTooltip, showTooltip } = useTooltip<TooltipData>()
  const svgRef = useRef<SVGSVGElement>(null)
  const { legendRef, legendOverflow } = useLegendOverflow()

  // bounds
  const xMax = width - margin.left
  const yMax = height - margin.top - 100 - legendOverflow

  // Both stacks share one ranking, taken across both periods, so a series sits
  // at the same height in the compared bar as in its neighbour.
  const ranking = useMemo(() => keysByTotalDesc([...data, ...comparedData]), [data, comparedData])
  const keys = useMemo(() => ranking.filter((key) => data.some((d) => has(d.values, key))), [ranking, data])
  const keys2 = useMemo(
    () => ranking.filter((key) => comparedData.some((d) => has(d.values, key))),
    [ranking, comparedData],
  )

  // The legend lists the primary series' keys plus any distinct key the second
  // series introduces. For period comparison the two carry identical keys, so
  // uniq collapses them and nothing changes.
  //
  // Goal comparison is excluded: it draws its bars a flat grey, so listing its
  // key would put a coloured swatch in the legend for bars that are not that
  // colour.
  const legendKeys = useMemo(
    () => (isGoalComparison ? keys : uniq([...keys, ...keys2])),
    [keys, keys2, isGoalComparison],
  )

  const yTotals = useMemo(() => {
    const barTotals = data.map(sumValues).concat(comparedData.map(sumValues))
    if (scheduledData) {
      scheduledData.forEach((d) => barTotals.push(d.value))
    }
    return barTotals
  }, [data, comparedData, scheduledData])

  // scales
  const dateScale = useMemo(
    () =>
      scaleBand<string>({
        domain: data?.map(getDate),
        padding: -0.00888888 * data.length + 0.962,
      }),
    [data],
  )

  const dateScale2 = useMemo(
    () =>
      scaleBand<string>({
        domain: comparedData.map(getDate),
        padding: -0.00888888 * comparedData.length + 0.962,
      }),
    [comparedData],
  )

  const yScale = useMemo(
    () =>
      scaleLinear<number>({
        // Reaching below zero lets a negative period draw. For data that is
        // entirely non-negative the floor stays 0, so the scale, the zero
        // line, and every bar are identical to before.
        domain: [Math.min(0, ...yTotals), Math.max(1, ...yTotals)],
        nice: true,
      }),
    [yTotals],
  )

  const fallbackColorScale = useMemo(
    () =>
      scaleOrdinal<string, string>({
        domain: allKeys,
        range: [...DEFAULT_CHART_HUES],
      }),
    [allKeys],
  )

  const getColor = useMemo(
    () => (key: string) => colorMap?.[key] ?? fallbackColorScale(key),
    [colorMap, fallbackColorScale],
  )

  dateScale.rangeRound([0, xMax])
  dateScale2.rangeRound([0, xMax])
  yScale.range([yMax, 0])

  // The stack returns a negative height for a bar that sits below zero, and
  // an SVG rect with a negative height draws nothing. Anchor both directions
  // to the zero line: the higher of the two edges is the top, and the height
  // is the distance between them. Positive bars come through untouched.
  const barRect = (bar: { y: number; height: number }) => ({
    y: Math.min(bar.y, bar.y + bar.height),
    height: Math.abs(bar.height),
  })

  // A pair straddles the centre of its band: one bar starts half the gap to the
  // right of that centre, the other ends half the gap to its left. Unpinned,
  // the gap falls out of the band's padding, so it shrinks as buckets are added.
  const halfPairGap = pairGap != null ? pairGap / 2 : dateScale.paddingOuter() * 4

  const labelFontSize = useMemo(() => {
    const step = dateScale.step()
    if (!comparedData.length) {
      if (step < 30) return null
      if (step < 45) return 10
      return 12
    }
    const spacePerLabel = step / 2
    if (spacePerLabel < 28) return null
    if (spacePerLabel < 34) return 10
    return 12
  }, [dateScale, dateScale2, comparedData.length])

  // The bucket holding today is the present even before anything in it completes
  // (the first of the month), so its completed slot stays left of the line.
  const currentBucket = useMemo(() => {
    if (!now) return undefined
    const domain = dateScale.domain()
    return domain.find((d, i) => {
      const nextDate = domain[i + 1]
      if (!nextDate) {
        return moment(now).isAfter(d) || moment(now).isSame(d, `day`)
      }
      return moment(now).isBetween(d, nextDate, `day`, `[)`)
    })
  }, [dateScale, now])

  const nowLineX = useMemo(() => {
    if (!currentBucket) return null

    const barX = dateScale(currentBucket)!
    const barWidth = dateScale.bandwidth()
    const completedRightEdge = comparedData.length ? barX + barWidth * 1.5 + halfPairGap : barX + barWidth
    const scheduledLeftEdge = comparedData.length
      ? barX + barWidth * 1.5 + 3 * halfPairGap
      : barX + barWidth + halfPairGap
    return (completedRightEdge + scheduledLeftEdge) / 2
  }, [dateScale, width, currentBucket, comparedData])

  return width < 10 ? null : (
    <div style={{ position: `relative` }}>
      <svg ref={svgRef} width={width} height={height} data-testid="grouped-bar-stack-graph">
        {/* `style`, not the `fill` attribute: `background` is `var(--surface)`, which only
            resolves reliably as CSS, not as an SVG presentation attribute. */}
        <rect x={0} y={0} width={width} height={height} style={{ fill: background }} rx={14} />
        {/* `PatternLines` sets `stroke`/`background` as plain attributes with no `style` escape
            hatch, so it is redrawn by hand here (same 8x8 diagonal pattern) with `style` colors. */}
        <Pattern id="lines" width={8} height={8}>
          <rect width={8} height={8} style={{ fill: `var(--surface)` }} />
          <path
            d="M 0,8 l 8,-8 M -2,2 l 4,-4 M 6,10 l 4,-4"
            style={{ stroke: `var(--line-default)` }}
            strokeWidth={1}
            strokeLinecap="square"
          />
        </Pattern>
        {/* `lineStyle`, not `fill`: grid lines have no fill, and `lineStyle` lands in the <line>'s
            `style`, where `var()` resolves reliably (unlike a `stroke` attribute). */}
        <GridRows
          top={margin.top}
          left={margin.left}
          scale={yScale}
          width={xMax}
          height={yMax}
          lineStyle={{ stroke: `var(--line-subtle)` }}
          numTicks={5}
        />
        <Group top={margin.top} left={margin.left}>
          <BarStack<DataType, string>
            data={data}
            keys={keys}
            value={(d, key) => (has(d.values, key) ? d.values[key] : 0)}
            x={getDate}
            xScale={dateScale}
            yScale={yScale}
            color={getColor}
            rx={3}
          >
            {(barStacks) => {
              const capKey = capKeysByIndex(barStacks)
              const baseKey = baseKeysByIndex(barStacks)
              return barStacks?.map((barStack) =>
                barStack.bars?.map((bar) => (
                  <ChipSegment
                    key={`bar-stack-${barStack.index}-${bar.index}`}
                    x={comparedData.length ? bar.x + bar.width / 2 + halfPairGap : bar.x}
                    y={barRect(bar).y}
                    height={barRect(bar).height}
                    width={bar.width}
                    color={bar.color}
                    rounded={capKey[bar.index] === bar.key}
                    base={baseKey[bar.index] === bar.key}
                    onClick={() => {
                      if (events) alert(`clicked: ${JSON.stringify(bar)}`)
                    }}
                    onMouseLeave={() => {
                      tooltipTimeout = window.setTimeout(() => {
                        hideTooltip()
                      }, 300)
                    }}
                    onMouseMove={(event) => {
                      if (tooltipTimeout) clearTimeout(tooltipTimeout)
                      // TooltipInPortal expects coordinates to be relative to containerRef
                      // localPoint returns coordinates relative to the nearest SVG, which
                      // is what containerRef is set to in this example.
                      const coords = localPoint(svgRef.current!, event)
                      const left =
                        bar.x + bar.width + 180 > width
                          ? bar.x + bar.width - 180
                          : comparedData.length
                            ? bar.x + bar.width
                            : bar.x

                      showTooltip({
                        tooltipData: { type: `bar`, stack: `primary`, ...bar },
                        tooltipTop: coords?.y,
                        tooltipLeft: left + margin.left,
                      })
                    }}
                  />
                )),
              )
            }}
          </BarStack>
        </Group>
        {comparedData.length ? (
          <Group top={margin.top} left={margin.left}>
            <BarStack<DataType, string>
              data={comparedData}
              keys={keys2}
              value={(d, key) => (has(d.values, key) ? d.values[key] : 0)}
              x={getDate}
              xScale={dateScale2}
              yScale={yScale}
              color={isGoalComparison ? () => GOAL_HUE : getColor}
              rx={3}
            >
              {(barStacks) => {
                const capKey = capKeysByIndex(barStacks)
                const baseKey = baseKeysByIndex(barStacks)
                return barStacks?.map((barStack) =>
                  barStack.bars?.map((bar) => (
                    <ChipSegment
                      key={`bar-stack-${barStack.index}-${bar.index}`}
                      x={bar.x - bar.width / 2 - halfPairGap}
                      y={barRect(bar).y}
                      height={barRect(bar).height}
                      width={bar.width}
                      color={bar.color}
                      rounded={capKey[bar.index] === bar.key}
                      base={baseKey[bar.index] === bar.key}
                      onClick={() => {
                        if (events) alert(`clicked: ${JSON.stringify(bar)}`)
                      }}
                      onMouseLeave={() => {
                        tooltipTimeout = window.setTimeout(() => {
                          hideTooltip()
                        }, 300)
                      }}
                      onMouseMove={(event) => {
                        if (tooltipTimeout) clearTimeout(tooltipTimeout)
                        // TooltipInPortal expects coordinates to be relative to containerRef
                        // localPoint returns coordinates relative to the nearest SVG, which
                        // is what containerRef is set to in this example.
                        const coords = localPoint(svgRef.current!, event)
                        const left = bar.x + 180 > width ? bar.x - 180 : bar.x

                        showTooltip({
                          tooltipData: { type: `bar`, stack: `compared`, ...bar },
                          tooltipTop: coords?.y,
                          tooltipLeft: left + margin.left,
                        })
                      }}
                    />
                  )),
                )
              }}
            </BarStack>
            {!isGoalComparison && dimComparedSeries && (
              <>
                <ClipPath id={`clip-path-data2`}>
                  {comparedData.map((d) => (
                    <rect
                      key={d.day}
                      x={(dateScale2(d.day) ?? 0) - dateScale2.bandwidth() / 2 - halfPairGap}
                      y={yScale(sumValues(d))}
                      height={yMax - yScale(sumValues(d))}
                      width={dateScale2.bandwidth()}
                    />
                  ))}
                </ClipPath>
                <rect
                  x={0}
                  y={0}
                  fill={`url(#lines)`}
                  clipPath="url(#clip-path-data2)"
                  width={nowLineX ?? xMax}
                  height={yMax}
                  opacity={0.5}
                  className="pointer-events-none"
                />
              </>
            )}
          </Group>
        ) : null}
        {nowLineX != null ? (
          <Group top={margin.top} left={margin.left}>
            <rect
              x={nowLineX}
              y={0}
              style={{ fill: `var(--surface)` }}
              width={xMax - nowLineX}
              height={yMax}
              opacity={0.5}
              className="pointer-events-none"
            />
            {/* `fill` is a no-op on <Line> (it draws a stroked line, not a filled shape); the
                rendered color comes from `stroke-current`/`text-red-600` below. */}
            <Line from={{ x: nowLineX, y: 0 }} to={{ x: nowLineX, y: yMax }} className="stroke-current text-red-600" />
          </Group>
        ) : null}
        {/* numbers on top of compare bars */}
        {comparedData.length && labelFontSize != null ? (
          <Group top={margin.top} left={margin.left}>
            {comparedData.map((d) => {
              if (hideLabels) return null
              return (
                <Text
                  key={`bar-stack-compare-${d.day}`}
                  x={(dateScale2(d.day) ?? 0) - halfPairGap}
                  y={yScale(sumValues(d))}
                  fontSize={labelFontSize}
                  dy={sumValues(d) < 0 ? `1em` : `-.6em`}
                  textAnchor="middle"
                  opacity={dimComparedSeries ? 0.5 : undefined}
                  style={{ fontFamily: `Inter`, fill: `var(--fg-strong)` }}
                >
                  {`${formatValue(sumValues(d), valuePrefix)}`}
                </Text>
              )
            })}
          </Group>
        ) : null}

        {/* numbers on top of bars */}
        {labelFontSize != null && (
          <Group top={margin.top} left={margin.left}>
            {data?.map((d) => {
              if (hideLabels) return null
              return (
                <Text
                  key={`bar-stack-${d.day}`}
                  x={
                    comparedData.length
                      ? (dateScale(d.day) ?? 0) + dateScale.bandwidth() + halfPairGap
                      : (dateScale(d.day) ?? 0) + dateScale.bandwidth() / 2
                  }
                  y={yScale(sumValues(d))}
                  fontSize={labelFontSize}
                  dy={sumValues(d) < 0 ? `1em` : `-.6em`}
                  textAnchor="middle"
                  style={{ fontFamily: `Inter`, fill: `var(--fg-strong)` }}
                >
                  {`${formatValue(sumValues(d), valuePrefix)}`}
                </Text>
              )
            })}
          </Group>
        )}
        {/* scheduled bars */}
        {scheduledData?.length ? (
          <Group top={margin.top} left={margin.left}>
            {scheduledData.map((d) => {
              const barX = dateScale(d.day)
              if (barX == null) return null
              const barWidth = dateScale.bandwidth()
              const totalValue = d.value
              const barY = yScale(totalValue)
              // The current bucket keeps its completed slot, so scheduled sits right of the line.
              // Future buckets have nothing completed, so scheduled takes the completed slot.
              const xOffset =
                d.day === currentBucket
                  ? comparedData.length
                    ? barX + barWidth * 1.5 + 3 * halfPairGap
                    : barX + barWidth + halfPairGap
                  : comparedData.length
                    ? barX + barWidth / 2 + halfPairGap
                    : barX

              // Stack channel segments bottom-up
              let yOffset = yMax
              const segments = d.byChannel.filter((ch) => ch.count > 0)

              return (
                <g key={`scheduled-${d.day}`}>
                  {segments.map((ch, segmentIndex) => {
                    const segmentHeight = yMax - yScale(ch.count)
                    yOffset -= segmentHeight
                    return (
                      <ChipSegment
                        key={`scheduled-${d.day}-${ch.channel}`}
                        x={xOffset}
                        y={yOffset}
                        height={segmentHeight}
                        width={barWidth}
                        color={getColor(ch.channel)}
                        rounded={segmentIndex === segments.length - 1}
                        base={segmentIndex === 0}
                        onMouseLeave={() => {
                          tooltipTimeout = window.setTimeout(() => {
                            hideTooltip()
                          }, 300)
                        }}
                        onMouseMove={(event) => {
                          if (tooltipTimeout) clearTimeout(tooltipTimeout)
                          const coords = localPoint(svgRef.current!, event)
                          const left = xOffset + barWidth + 180 > width ? xOffset - 180 : xOffset + barWidth
                          showTooltip({
                            tooltipData: {
                              type: `scheduled`,
                              day: d.day,
                              tooltipDisplay: d.tooltipDisplay,
                              byChannel: d.byChannel,
                            },
                            tooltipTop: coords?.y,
                            tooltipLeft: left + margin.left,
                          })
                        }}
                      />
                    )
                  })}
                  {!hideLabels && labelFontSize != null && (
                    <Text
                      x={xOffset + barWidth / 2}
                      y={barY}
                      fontSize={labelFontSize}
                      dy={`-.6em`}
                      textAnchor="middle"
                      opacity={0.5}
                      style={{ fontFamily: `Inter`, fill: `var(--fg-strong)` }}
                    >
                      {`${formatValue(totalValue, valuePrefix)}`}
                    </Text>
                  )}
                </g>
              )
            })}
          </Group>
        ) : null}
        <AxisBottom
          top={yMax + margin.top}
          left={margin.left}
          scale={dateScale}
          numTicks={data.length}
          stroke={`d2d2d2`}
          tickStroke={`abcbca`}
          tickLabelProps={(value, i, values) => ({
            angle: -30,
            textAnchor: `end`,
            className: `fill-current text-body-xs text-strong`,
          })}
          hideAxisLine
        >
          {({ ticks }) => (
            <Tick
              ticks={ticks?.map((tick) => ({ ...tick, value: moment(tick.value, tickFormat).format(tickFormat) }))}
            />
          )}
        </AxisBottom>
        <Axis
          orientation={Orientation.left}
          top={margin.top}
          left={50}
          scale={yScale}
          tickFormat={(v) => formatValue(Number(v), valuePrefix)}
          stroke={`d2d2d2`}
          tickStroke={`abcbca`}
          numTicks={5}
          tickLabelProps={{
            className: `fill-current text-body-xs text-subtle`,
          }}
        />
      </svg>
      {/* Wraps on narrow widths; `useLegendOverflow` shrinks the plot when it outgrows its room. */}
      <div
        ref={legendRef}
        style={{
          position: `absolute`,
          bottom: 0,
          width: `100%`,
          display: `flex`,
          flexWrap: `wrap`,
          alignItems: `center`,
          justifyContent: `space-between`,
          rowGap: `4px`,
          fontSize: `14px`,
        }}
      >
        <CommonLegendOrdinal scale={fallbackColorScale} domain={legendKeys} totals={totals} getColor={getColor} />
        {bottomRightSlot && <div className="ml-auto">{bottomRightSlot}</div>}
      </div>

      {tooltipOpen && tooltipData && (
        <TooltipWithBounds
          top={tooltipTop}
          left={tooltipLeft}
          style={tooltipStyles}
          offsetLeft={16}
          offsetTop={12}
          className="graph-tooltip-wrapper"
        >
          {tooltipData.type === `bar` ? (
            <>
              <div className="text-ui-xs inverse-text-strong">
                {tooltipData.bar.data.tooltipDisplay ?? tooltipData.bar.data.day}
              </div>
              {stackTopDown(tooltipData.stack === `compared` ? keys2 : keys, tooltipData.bar.data.values).map(
                ([key, value]) => (
                  <div key={key} className="mt-2 flex items-center">
                    <div
                      style={{
                        backgroundColor: seriesStyle(
                          tooltipData.stack === `compared` && isGoalComparison ? GOAL_HUE : getColor(key),
                        ).fill,
                        borderColor: seriesStyle(
                          tooltipData.stack === `compared` && isGoalComparison ? GOAL_HUE : getColor(key),
                        ).stroke,
                      }}
                      className="h-2 w-2 rounded-full border"
                    ></div>
                    <div className="ml-1.5 text-body-xs inverse-text-default">
                      {key} {formatValue(value as number, valuePrefix)}
                    </div>
                  </div>
                ),
              )}
            </>
          ) : (
            <>
              <div className="text-ui-xs inverse-text-strong">
                Scheduled — {tooltipData.tooltipDisplay ?? tooltipData.day}
              </div>
              {tooltipData.byChannel.map((ch) => (
                <div key={ch.channel} className="mt-2 flex justify-between">
                  <div className="flex items-center">
                    <div
                      style={{
                        backgroundColor: seriesStyle(getColor(ch.channel)).fill,
                        borderColor: seriesStyle(getColor(ch.channel)).stroke,
                      }}
                      className="h-2 w-2 rounded-full border"
                    ></div>
                    <div className="ml-1.5 text-body-xs inverse-text-default">{ch.channel}</div>
                  </div>
                  <div className="text-body-xs inverse-text-default">{formatValue(ch.count, valuePrefix)}</div>
                </div>
              ))}
            </>
          )}
        </TooltipWithBounds>
      )}
    </div>
  )
}
